import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CRIT_LABELS, CRIT_COLORS } from "./ReadinessChecklist";
import { ClipboardList, Plus, Trash2, Link2, Loader2, ChevronDown, FileDown } from "lucide-react";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const STATUS_META = { ready: ["Ready", "bg-emerald-100 text-emerald-800"], minor: ["Minor improvement", "bg-sky-100 text-sky-800"], major: ["Major improvement", "bg-amber-100 text-amber-800"], missing: ["Missing", "bg-red-100 text-red-700"], na: ["Not applicable", "bg-zinc-100 text-zinc-500"] };
const RESP_LABELS = { owner: "Property owner", happy_retreats: "Happy Retreats", shared: "Shared", operating_expense: "Operating expense", third_party: "Third-party vendor", tbd: "To be decided" };
const DECISION_LABELS = { approved: "Approved for onboarding", approved_conditional: "Approved subject to conditions", reassessment_required: "Reassessment required", restructure_commercials: "Commercial structure must be revised", postponed: "Launch postponed", rejected: "Property rejected" };
const PROFILE_FIELDS = [["property_name", "Property name"], ["owner_name", "Owner name"], ["ownership_entity", "Ownership entity"], ["address", "Property address"], ["maps_link", "Google Maps location"], ["land_extent", "Land extent"], ["built_up_area", "Built-up area"], ["landscaped_area", "Landscaped area"], ["event_space_area", "Outdoor event-space area"], ["buildings", "Number of buildings"], ["rooms", "Number of rooms"], ["villas", "Number of villas"], ["pools", "Number of pools"], ["kitchens", "Number of kitchens"], ["stay_capacity", "Maximum stay capacity"], ["event_capacity", "Maximum event capacity"], ["operating_status", "Current operating status"], ["expected_launch", "Expected launch date"], ["property_age", "Property age"], ["existing_operator", "Existing operator"], ["existing_brand", "Existing brand name"], ["online_listings", "Existing online listings"], ["existing_revenue", "Existing revenue (if operational)"]];
const OWNER_FIELDS = [["expected_return", "Expected monthly return"], ["preferred_model", "Preferred partnership model"], ["invest_willingness", "Willingness to invest before launch"], ["investment_budget", "Available investment budget"], ["returns_start", "Expected date for returns to begin"], ["involvement", "Preferred involvement level"], ["long_term", "Long-term plans"], ["no_change_areas", "Areas the owner does not want changed"], ["loans", "Existing loans or obligations"]];

export function AssessmentsList() {
  const [rows, setRows] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/admin/readiness/assessments").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  if (!rows) return <div className="p-8 text-zinc-400">Loading…</div>;
  return (
    <div className="p-6 md:p-8" data-testid="readiness-assessments">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><ClipboardList className="h-6 w-6" /> Property Readiness Assessments</h1>
          <p className="text-sm text-zinc-500 mt-1">Guided assessments with scoring, launch-gate logic, investment allocation and owner reports. <Link to="/admin/property-readiness/master-checklist" className="underline">Manage master checklist</Link></p>
        </div>
        <Button onClick={async () => { const { data } = await api.post("/admin/readiness/assessments", {}); nav(`/admin/property-readiness/assessments/${data.id}`); }} data-testid="rd-assess-new">
          <Plus className="w-4 h-4 mr-1" /> New assessment
        </Button>
      </div>
      <div className="border rounded-xl bg-white divide-y">
        {rows.map((a) => {
          const c = a.computed || {};
          return (
            <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`rd-assess-row-${a.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium">{a.profile?.property_name || "Untitled property"} <span className="text-xs text-zinc-400">v{a.version}</span></div>
                <div className="text-[11px] text-zinc-400">{a.profile?.property_type || "—"} · {a.profile?.property_stage || "—"} · {String(a.updated_at || "").slice(0, 10)} · {a.assessor}</div>
              </div>
              {c.score !== undefined && <span className="text-sm font-semibold tabular-nums">{c.score}%</span>}
              {c.classification_label && <span className={`text-[10px] border rounded px-2 py-0.5 ${c.classification === "ready" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : c.classification === "blocked" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.classification_label}</span>}
              {a.decision && <span className="text-[10px] border rounded px-2 py-0.5 bg-zinc-100">{DECISION_LABELS[a.decision.status]}</span>}
              <Button size="sm" variant="outline" onClick={() => nav(`/admin/property-readiness/assessments/${a.id}`)} data-testid={`rd-assess-open-${a.id}`}>Open</Button>
              <Button size="sm" variant="outline" onClick={async () => { const { data } = await api.post(`/admin/readiness/assessments/${a.id}/version`); load(); toast.success(`Version ${data.version} created`); }}>New version</Button>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/property-readiness/${a.share_token}`); toast.success("Owner report link copied"); }} data-testid={`rd-assess-share-${a.id}`}><Link2 className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={async () => { if (!window.confirm("Delete this assessment?")) return; await api.delete(`/admin/readiness/assessments/${a.id}`); load(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          );
        })}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-400">No assessments yet — create the first one.</div>}
      </div>
    </div>
  );
}

const STEPS = ["Property profile", "Template & categories", "Assess items", "Review & decide"];

export default function AssessmentEditor() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cats, setCats] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [itemsByCat, setItemsByCat] = useState({});
  const [step, setStep] = useState(0);
  const [openCat, setOpenCat] = useState(null);
  const [itemFilter, setItemFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/admin/readiness/assessments/${id}`), api.get("/admin/readiness/meta"), api.get("/admin/readiness/categories"), api.get("/admin/readiness/templates")])
      .then(([ar, mr, cr, tr]) => { setA(ar.data); setMeta(mr.data); setCats(cr.data.filter((c) => !c.hidden && c.active !== false)); setTemplates(tr.data); });
  }, [id]);

  const loadItems = async (cid) => {
    if (itemsByCat[cid]) return;
    const { data } = await api.get(`/admin/readiness/items?category_id=${cid}`);
    setItemsByCat((p) => ({ ...p, [cid]: data.filter((i) => !i.hidden && i.active !== false) }));
  };
  useEffect(() => { if (a && step === 2) a.selected_category_ids.forEach(loadItems); }, [a, step]); // eslint-disable-line

  const save = async (extra = {}, ev = "updated") => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/readiness/assessments/${id}`, {
        profile: a.profile, template_id: a.template_id, selected_category_ids: a.selected_category_ids, items: a.items, event: ev, ...extra,
      });
      setA(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const setProfile = (k, v) => setA((p) => ({ ...p, profile: { ...p.profile, [k]: v } }));
  const setAns = (iid, k, v) => setA((p) => ({ ...p, items: { ...p.items, [iid]: { ...(p.items[iid] || {}), [k]: v } } }));

  const applicableItems = (cid) => (itemsByCat[cid] || []).filter((it) => {
    const t = a.profile?.property_type, s = a.profile?.property_stage;
    if ((it.types || []).length && t && !it.types.includes(t)) return false;
    if ((it.stages || []).length && s && !it.stages.includes(s)) return false;
    return true;
  });

  if (!a || !meta) return <div className="p-8 text-zinc-400">Loading…</div>;
  const comp = a.computed || {};
  const isAdmin = meta.role === "admin";

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="readiness-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold">{a.profile?.property_name || "New assessment"} <span className="text-sm text-zinc-400">v{a.version}</span></h1>
          {a.previous && <div className="text-[11px] text-zinc-400">Previous v{a.previous.version}: score {a.previous.score}% · investment {inr(a.previous.investment)}</div>}
        </div>
        <div className="flex items-center gap-2">
          {comp.score !== undefined && <span className="text-sm font-semibold" data-testid="rd-score">{comp.score}% · {comp.classification_label}</span>}
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/property-readiness/${a.share_token}`); toast.success("Owner link copied"); }}>Owner link</Button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/readiness/report/${a.share_token}/pdf`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><FileDown className="w-3.5 h-3.5 mr-1" /> Owner PDF</Button></a>
          <Button size="sm" onClick={() => save()} disabled={saving} data-testid="rd-save">{saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save</Button>
        </div>
      </div>
      <div className="flex gap-1 mb-5 border-b overflow-x-auto">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px ${step === i ? "border-zinc-900 font-medium" : "border-transparent text-zinc-400"}`} data-testid={`rd-step-${i}`}>{i + 1}. {s}</button>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-5">
          <div className="bg-white border rounded-xl p-5">
            <div className="text-sm font-semibold mb-3">Ownership & property details</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block"><span className="text-[11px] uppercase text-zinc-500">Property stage</span>
                <select value={a.profile?.property_stage || ""} onChange={(e) => setProfile("property_stage", e.target.value)} className="mt-1 w-full border rounded-md px-2 py-2 text-sm bg-white" data-testid="rd-profile-stage">
                  <option value="">Select…</option>{meta.property_stages.map((s) => <option key={s}>{s}</option>)}
                </select></label>
              <label className="block"><span className="text-[11px] uppercase text-zinc-500">Property type</span>
                <select value={a.profile?.property_type || ""} onChange={(e) => setProfile("property_type", e.target.value)} className="mt-1 w-full border rounded-md px-2 py-2 text-sm bg-white" data-testid="rd-profile-type">
                  <option value="">Select…</option>{meta.property_types.map((s) => <option key={s}>{s}</option>)}
                </select></label>
              <div />
              {PROFILE_FIELDS.map(([k, l]) => (
                <label key={k} className="block"><span className="text-[11px] uppercase text-zinc-500">{l}</span>
                  <Input value={a.profile?.[k] || ""} onChange={(e) => setProfile(k, e.target.value)} className="mt-1 text-sm" data-testid={`rd-profile-${k}`} /></label>
              ))}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-5">
            <div className="text-sm font-semibold mb-3">Owner expectations</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {OWNER_FIELDS.map(([k, l]) => (
                <label key={k} className="block"><span className="text-[11px] uppercase text-zinc-500">{l}</span>
                  <Input value={a.profile?.[k] || ""} onChange={(e) => setProfile(k, e.target.value)} className="mt-1 text-sm" /></label>
              ))}
            </div>
          </div>
          <Button onClick={() => { save(); setStep(1); }} data-testid="rd-next-1">Continue → Template & categories</Button>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <div className="bg-white border rounded-xl p-5">
            <div className="text-sm font-semibold mb-2">Assessment template</div>
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <button key={t.id} onClick={() => setA((p) => ({ ...p, template_id: t.id, selected_category_ids: t.category_ids.filter((cid) => cats.some((c) => c.id === cid)) }))}
                  className={`px-3 py-2 text-xs border rounded-md ${a.template_id === t.id ? "bg-zinc-900 text-white" : "bg-white"}`} data-testid={`rd-template-${t.id}`}>{t.name}</button>
              ))}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Applicable categories ({a.selected_category_ids.length} selected)</div>
              <div className="flex gap-2 text-xs">
                <button className="underline" onClick={() => setA((p) => ({ ...p, selected_category_ids: cats.map((c) => c.id) }))}>Select all</button>
                <button className="underline" onClick={() => setA((p) => ({ ...p, selected_category_ids: [] }))}>Deselect all</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {cats.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                  <input type="checkbox" checked={a.selected_category_ids.includes(c.id)} onChange={(e) => setA((p) => ({ ...p, selected_category_ids: e.target.checked ? [...p.selected_category_ids, c.id] : p.selected_category_ids.filter((x) => x !== c.id) }))} data-testid={`rd-selcat-${c.id}`} />
                  <span className="flex-1">{c.name}</span>
                  <span className="text-[10px] text-zinc-400">{c.counters?.total ?? ""} items{c.launch_gate ? " · gate" : ""}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={() => { save(); setStep(2); }} data-testid="rd-next-2">Continue → Assess items</Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex gap-2 text-xs items-center">
            <span className="text-zinc-500">Show:</span>
            {[["all", "All"], ["blockers", "Launch blockers"], ["mandatory", "Mandatory"], ["incomplete", "Incomplete"]].map(([k, l]) => (
              <button key={k} onClick={() => setItemFilter(k)} className={`px-2.5 py-1.5 border rounded ${itemFilter === k ? "bg-zinc-900 text-white" : "bg-white"}`} data-testid={`rd-itemfilter-${k}`}>{l}</button>
            ))}
          </div>
          {a.selected_category_ids.map((cid) => {
            const c = cats.find((x) => x.id === cid);
            if (!c) return null;
            const its = applicableItems(cid).filter((it) => {
              const ans = a.items[it.id] || {};
              if (itemFilter === "blockers") return it.criticality === "launch_blocker";
              if (itemFilter === "mandatory") return ["launch_blocker", "mandatory_pre", "mandatory_post"].includes(it.criticality);
              if (itemFilter === "incomplete") return ans.applicable !== false && !ans.status;
              return true;
            });
            const done = applicableItems(cid).filter((it) => (a.items[it.id] || {}).status).length;
            return (
              <div key={cid} className="bg-white border rounded-xl">
                <button className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium" onClick={() => setOpenCat(openCat === cid ? null : cid)} data-testid={`rd-acat-${cid}`}>
                  <span>{c.name}</span>
                  <span className="text-xs text-zinc-400 flex items-center gap-2">{done}/{applicableItems(cid).length} assessed <ChevronDown className={`w-4 h-4 transition-transform ${openCat === cid ? "rotate-180" : ""}`} /></span>
                </button>
                {openCat === cid && (
                  <div className="border-t divide-y">
                    {its.map((it) => {
                      const ans = a.items[it.id] || {};
                      const na = ans.applicable === false;
                      return (
                        <div key={it.id} className={`px-4 py-3 ${na ? "opacity-40" : ""}`} data-testid={`rd-aitem-${it.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <input type="checkbox" checked={!na} onChange={(e) => setAns(it.id, "applicable", e.target.checked)} title="Applicable to this assessment" data-testid={`rd-aitem-applicable-${it.id}`} />
                            <span className="text-sm flex-1 min-w-[160px]">{it.name}</span>
                            <span className={`text-[10px] border rounded px-1.5 py-0.5 ${CRIT_COLORS[it.criticality]}`}>{CRIT_LABELS[it.criticality]}</span>
                            {!na && Object.entries(STATUS_META).map(([k, [l, cls]]) => (
                              <button key={k} onClick={() => setAns(it.id, "status", k)} className={`text-[10px] rounded px-2 py-1 border ${ans.status === k ? cls + " border-transparent font-semibold" : "bg-white text-zinc-400"}`} data-testid={`rd-status-${it.id}-${k}`}>{l}</button>
                            ))}
                          </div>
                          {!na && ans.status && ans.status !== "na" && (
                            <div className="grid sm:grid-cols-4 gap-2 mt-2">
                              <Input placeholder="Estimated cost ₹" type="number" value={ans.cost || ""} onChange={(e) => setAns(it.id, "cost", e.target.value)} className="text-xs" data-testid={`rd-cost-${it.id}`} />
                              <select value={ans.responsibility || ""} onChange={(e) => setAns(it.id, "responsibility", e.target.value)} className="border rounded-md px-2 py-2 text-xs bg-white" data-testid={`rd-resp-${it.id}`}>
                                <option value="">Responsibility…</option>{Object.entries(RESP_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                              </select>
                              {ans.responsibility === "shared" && <Input placeholder="Owner share %" type="number" value={ans.share_owner_pct || ""} onChange={(e) => setAns(it.id, "share_owner_pct", e.target.value)} className="text-xs" data-testid={`rd-share-${it.id}`} />}
                              <Input placeholder="Deadline" type="date" value={ans.deadline || ""} onChange={(e) => setAns(it.id, "deadline", e.target.value)} className="text-xs" />
                              <Input placeholder="Recommended action" value={ans.action || ""} onChange={(e) => setAns(it.id, "action", e.target.value)} className="text-xs sm:col-span-2" />
                              <Input placeholder="Evidence link / reference" value={ans.evidence || ""} onChange={(e) => setAns(it.id, "evidence", e.target.value)} className="text-xs sm:col-span-2" />
                              <Textarea rows={1} placeholder="Owner-visible note" value={ans.owner_remarks || ""} onChange={(e) => setAns(it.id, "owner_remarks", e.target.value)} className="text-xs sm:col-span-2" />
                              <Textarea rows={1} placeholder="Internal-only note (never shown to owner)" value={ans.internal_note || ""} onChange={(e) => setAns(it.id, "internal_note", e.target.value)} className="text-xs sm:col-span-2" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="sticky bottom-3 flex gap-2">
            <Button onClick={() => save()} disabled={saving} data-testid="rd-save-items">{saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save progress</Button>
            <Button variant="outline" onClick={() => { save({ status: "submitted" }, "submitted for review"); setStep(3); }} data-testid="rd-submit">Submit for review →</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="bg-white border rounded-xl p-4"><div className="text-[10px] uppercase text-zinc-400">Readiness score</div><div className="text-2xl font-semibold" data-testid="rd-final-score">{comp.score ?? "—"}%</div><div className="text-xs mt-0.5">{comp.classification_label}</div></div>
            <div className="bg-white border rounded-xl p-4"><div className="text-[10px] uppercase text-zinc-400">Launch blockers</div><div className={`text-2xl font-semibold ${comp.blockers?.length ? "text-red-600" : "text-emerald-600"}`} data-testid="rd-final-blockers">{comp.blockers?.length ?? 0}</div></div>
            <div className="bg-white border rounded-xl p-4"><div className="text-[10px] uppercase text-zinc-400">Total investment</div><div className="text-2xl font-semibold">{inr(comp.investment?.total)}</div></div>
            <div className="bg-white border rounded-xl p-4"><div className="text-[10px] uppercase text-zinc-400">Items assessed</div><div className="text-2xl font-semibold">{comp.counts?.assessed ?? 0}</div></div>
          </div>
          {comp.blockers?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="text-sm font-semibold text-red-700 mb-2">Unresolved launch blockers — property cannot receive launch approval</div>
              <ul className="text-xs text-red-700 space-y-1">{comp.blockers.map((b, i) => <li key={i}>· {b.name} ({b.category}) {b.cost ? `— ${inr(b.cost)}` : ""}</li>)}</ul>
            </div>
          )}
          <div className="bg-white border rounded-xl p-5">
            <div className="text-sm font-semibold mb-3">Investment allocation</div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              {[["owner", "Owner-funded"], ["happy_retreats", "Happy Retreats-funded"], ["shared_owner", "Shared — owner part"], ["shared_hr", "Shared — HR part"], ["operating_expense", "Operating expense"], ["tbd", "To be decided"], ["launch_blocker", "Launch-blocker work"], ["mandatory_pre", "Mandatory pre-launch"], ["mandatory_post", "Mandatory post-launch"], ["recommended", "Recommended"], ["optional", "Optional"], ["total", "TOTAL"]].map(([k, l]) => (
                <div key={k} className="flex justify-between border-b pb-1"><span className="text-zinc-500">{l}</span><span className={`tabular-nums ${k === "total" ? "font-bold" : ""}`} data-testid={`rd-inv-${k}`}>{inr(comp.investment?.[k])}</span></div>
              ))}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-5">
            <div className="text-sm font-semibold mb-2">Category scores</div>
            <div className="grid sm:grid-cols-2 gap-1.5 text-xs">
              {Object.values(comp.category_scores || {}).map((cs, i) => (
                <div key={i} className="flex justify-between border-b pb-1"><span>{cs.name} <span className="text-zinc-400">(w{cs.weight}%)</span></span><span className="tabular-nums font-medium">{cs.score}%</span></div>
              ))}
            </div>
          </div>
          {isAdmin && (
            <div className="bg-white border rounded-xl p-5" data-testid="rd-decision-panel">
              <div className="text-sm font-semibold mb-3">Final onboarding decision (admin only)</div>
              {a.decision && <div className="text-xs mb-3 bg-zinc-50 border rounded p-2">Current: <strong>{DECISION_LABELS[a.decision.status]}</strong> by {a.decision.by} on {String(a.decision.at).slice(0, 10)}{a.decision.reason ? ` — ${a.decision.reason}` : ""}</div>}
              <DecisionForm id={id} onDone={(d) => setA(d)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DecisionForm({ id, onDone }) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  return (
    <div className="flex flex-wrap items-end gap-3">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-md px-2 py-2 text-sm bg-white" data-testid="rd-decision-select">
        <option value="">Select decision…</option>
        {Object.entries(DECISION_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Input placeholder="Reason (required for rejection / postponement)" value={reason} onChange={(e) => setReason(e.target.value)} className="text-sm w-96" data-testid="rd-decision-reason" />
      <Button onClick={async () => {
        if (!status) return toast.error("Select a decision");
        try {
          const { data } = await api.post(`/admin/readiness/assessments/${id}/decision`, { status, reason });
          onDone(data); toast.success("Decision recorded");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      }} data-testid="rd-decision-save">Record decision</Button>
    </div>
  );
}
